import { Box } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../core-components/theme/components/Buttons';
import { InlineAlert } from '../../../core-components/theme/components/InlineAlert';
import { emptyRegionSx } from '../../../core-components/theme/components/Screen';
import type { FormProblem } from '../domain/refusal';
import type { HoldView } from '../hooks/logic/loginView';

import { HoldNotice } from './HoldNotice';
import { formProblemText } from './refusalText';

export interface LoginNoticeProps {
  readonly waiting: boolean;
  readonly hold: HoldView | null;
  readonly problem: FormProblem | null;
  readonly retryInline: boolean;
  readonly passwordOffered: boolean;
  readonly onRetry: () => void;
}

const isRetryable = (problem: FormProblem): boolean => problem.kind === 'transport' || problem.retryable;

const ProblemAlert: React.FC<{ readonly problem: FormProblem; readonly retryInline: boolean; readonly onRetry: () => void }> = ({
  problem,
  retryInline,
  onRetry,
}) => {
  const { t } = useTranslation();
  const retry = retryInline && isRetryable(problem);
  return (
    <InlineAlert
      severity="error"
      role="alert"
      message={formProblemText(t, problem)}
      action={
        retry ? (
          <Button type="button" intent="tertiary" onClick={onRetry}>
            {t('common.tryAgain', 'Try again')}
          </Button>
        ) : undefined
      }
    />
  );
};

export const LoginNotice: React.FC<LoginNoticeProps> = ({ waiting, hold, problem, retryInline, passwordOffered, onRetry }) => {
  const { t } = useTranslation();
  const passwordLine = passwordOffered ? t('passcodeAuth.hold.usePasswordInstead', 'You can still log in with your password.') : null;
  return (
    <>
      <Box role="status" sx={emptyRegionSx}>
        {waiting && <InlineAlert severity="info" message={t('common.waitingForConnection', 'Waiting for the connection to come back…')} />}
      </Box>
      <HoldNotice hold={hold} extra={passwordLine} />
      {!waiting && hold === null && problem !== null && <ProblemAlert problem={problem} retryInline={retryInline} onRetry={onRetry} />}
    </>
  );
};
